import { Booking } from '@deliveries-app/models';
import {
  bookingService,
  IndexFilters,
  PostBooking,
  ProofOfDeliveryPayload,
  Reschedule,
} from '@deliveries-app/services';
import moment from 'moment-timezone';
import toast from 'react-hot-toast';
import { BookingActionTypes } from './actionTypes';

function showDurationFallbackWarning(warning: string | undefined) {
  if (warning && warning.trim()) {
    toast(warning, { icon: 'ℹ️', duration: 6000 });
  }
}

/**
 * Get all bookings action
 *
 * @param {number} branch_id
 * @param {IndexFilters} filters
 *
 * @returns {Promise<void>}
 */
export function getBookings(branch_id: number, filters?: IndexFilters) {
  console.log(`Dispatch getBookings action for branch id ${branch_id}`);

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const payload = await bookingService.getAllBookings(branch_id, filters);

      dispatch({
        type: BookingActionTypes.GET_ALL_BOOKINGS_SUCCESS,
        payload: payload,
      });
    } catch (error) {
      console.log('Get all bookings failed', error);
      dispatch({
        type: BookingActionTypes.GET_ALL_BOOKINGS_FAILURE,
        payload: error,
      });
    }
  };
}
/**
 * Get draft bookings action
 *
 * @param {number} branch_id
 * @param {IndexFilters} filters
 *
 * @returns {Promise<void>}
 */
export function getDraftBookings(branch_id: number, filters?: IndexFilters) {
  console.log(`Dispatch getDraftBookings action for branch id ${branch_id}`);

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const payload = await bookingService.getDraftBookings(branch_id, filters);

      dispatch({
        type: BookingActionTypes.GET_ALL_BOOKINGS_SUCCESS,
        payload: payload,
      });
    } catch (error) {
      console.log('Get draft bookings failed', error);
      dispatch({
        type: BookingActionTypes.GET_ALL_BOOKINGS_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Get all bookings with time status action
 *
 * @param {number} branch_id
 * @param {IndexFilters} filters
 *
 * @returns {Promise<void>}
 */
export function getBookingsTimeStatus(
  branch_id: number,
  filters?: IndexFilters
) {
  console.log(
    `Dispatch getBookingsTimeStatus action for branch id ${branch_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const payload = await bookingService.getAllBookingsWithTimeStatus(
        branch_id,
        filters
      );

      dispatch({
        type: BookingActionTypes.GET_ALL_BOOKINGS_SUCCESS,
        payload: payload,
      });
    } catch (error) {
      console.log('Get all bookings with time status failed', error);
      dispatch({
        type: BookingActionTypes.GET_ALL_BOOKINGS_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Create a new booking
 *
 * @param {number} branch_id
 * @param {PostBooking} data
 *
 * @returns {Promise<void>}
 */
export function createBooking(branch_id: number, data) {
  console.log(`Dispatch createBooking action for branch id ${branch_id}`);

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const response = await bookingService.createBooking(branch_id, data);

      dispatch({
        type: BookingActionTypes.CREATE_BOOKING_SUCCESS,
        payload: response.data,
      });
      showDurationFallbackWarning(response.warning);
    } catch (error) {
      dispatch({
        type: BookingActionTypes.CREATE_BOOKING_FAILURE,
        payload: {
          error: error.error ?? error,
          errors: error.errors ?? [],
        },
      });
    }
  };
}

/**
 * Update booking by id
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {Booking} booking
 *
 * @returns {Promise<void>}
 */
export function updateBooking(
  branch_id: number,
  booking_id: number,
  booking: Booking
) {
  console.log(
    `Dispatch updateBooking action for branch id ${branch_id} and booking id ${booking_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const response = await bookingService.updateBookingById(
        branch_id,
        booking_id,
        {
          delivery_instructions: booking.delivery_instructions,
          company_name: booking.company_name,
          status: booking.status,
          delay_time: booking.delay_time,
          delivery_fee: booking.financial.delivery_fee,
          invoice_number: booking.financial.invoice_number,
          order_number: booking.order_number,
          onsite_contact_name: booking.onsite_contact_name,
          onsite_contact_phone: booking.onsite_contact_phone,
          customer_type: booking.sale_type,
          loading_instructions: booking.loading_instructions,
          name: booking.delivery_location.name,
          address: booking.delivery_location.address,
          phone: booking.delivery_location.phone,
          email: booking.delivery_location.email,
          city: booking.delivery_location.city,
          suburb: booking.delivery_location.suburb,
          instructions: booking.delivery_instructions,
          // card_number: booking.card_number,
          // card_brand: booking.card_brand,
          // card_expiration: booking.card_expiration,
          // products: booking.products,
          delay_type: booking.delay_type,
          latitude: booking.delivery_location.raw_address.latitude,
          longitude: booking.delivery_location.raw_address.longitude,
          vehicle_id: booking.vehicle_id,
          start_time: moment(booking.start_time)
            .tz(booking.branch.timezone)
            .format('MM/DD/YYYY HH:mm'),
          sms: booking.sms === 0 ? false : true,
          products: booking.booking_line,
          photo: booking.photo,
          total_price: booking.financial.total_price,
          anytime: booking.anytime,
          internal_notes: booking.internal_notes,
          non_chargeable: booking.non_chargeable,
        }
      );

      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
        payload: booking,
      });
      showDurationFallbackWarning(response.warning);
    } catch (error) {
      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Delete booking by id
 *
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<void>}
 */
export function deleteBooking(branch_id: number, booking_id: number) {
  console.log(
    `Dispatch deleteBooking action for branch id ${branch_id} and booking id ${booking_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      await bookingService.deleteBookingById(branch_id, booking_id);

      dispatch({
        type: BookingActionTypes.DELETE_BOOKING_SUCCESS,
        payload: booking_id,
      });
    } catch (error) {
      dispatch({
        type: BookingActionTypes.DELETE_BOOKING_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Create a new booking note
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {string} note
 *
 * @returns {Promise<void>}
 */
export function createBookingNote(
  branch_id: number,
  booking_id: number,
  content: string
) {
  console.log(
    `Dispatch createBookingNote action for branch id ${branch_id} and booking id ${booking_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const note = await bookingService.addBookingNotesById(
        branch_id,
        booking_id,
        { content: content }
      );

      dispatch({
        type: BookingActionTypes.CREATE_BOOKING_NOTE_SUCCESS,
        payload: {
          booking_id: booking_id,
          note: note,
        },
      });
    } catch (error) {
      dispatch({
        type: BookingActionTypes.CREATE_BOOKING_NOTE_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Update booking SMS setting
 *
 * @param {number} branch_id
 * @param {Booking} booking
 * @param {boolean} sms
 *
 * @returns {Promise<void>}
 */
export function updateBookingSMS(
  branch_id: number,
  booking: Booking,
  sms: boolean
) {
  console.log(
    `Dispatch updateBookingSMS action for branch id ${branch_id} and booking id ${booking.id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const res = await bookingService.updateBookingSmsSetting(
        branch_id,
        booking.id,
        sms
      );

      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
        payload: res,
      });
    } catch (error) {
      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Update booking driver
 *
 * @param {number} branch_id
 * @param {Booking} booking
 * @param {number} driver_id
 *
 * @returns {Promise<void>}
 */
export function updateBookingDriver(
  branch_id: number,
  booking: Booking,
  driver_id: number
) {
  console.log(
    `Dispatch updateBookingDriver action for branch id ${branch_id} and booking id ${booking.id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const res = await bookingService.updateBookingDriverById(
        branch_id,
        booking.id,
        driver_id
      );

      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
        payload: res,
      });
    } catch (error) {
      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Upload proof of delivery to booking
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {ProofOfDeliveryPayload} payload
 *
 * @returns {Promise<void>}
 */
export function uploadProofOfDelivery(
  branch_id: number,
  booking_id: number,
  payload: ProofOfDeliveryPayload
) {
  console.log(
    `Dispatch uploadProofOfDelivery action for branch id ${branch_id} and booking id ${booking_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const booking = await bookingService.storeProofOfDelivery(
        branch_id,
        booking_id,
        payload
      );

      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
        payload: booking,
      });
    } catch (error) {
      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Reschedule a booking
 *
 * @param {number} branch_id
 * @param {number} booking_id
 * @param {Reschedule} payload
 *
 * @returns {Promise<void>}
 */
export function rescheduleBooking(
  branch_id: number,
  booking_id: number,
  payload: Reschedule
) {
  console.log(
    `Dispatch rescheduleBooking action for branch id ${branch_id} and booking id ${booking_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const response = await bookingService.rescheduleBookingById(
        branch_id,
        booking_id,
        payload
      );

      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
        payload: response.data,
      });
      showDurationFallbackWarning(response.warning);
    } catch (error) {
      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_FAILURE,
        payload: {
          error: error.error ?? error,
          errors: error.errors ?? [],
        },
      });

      // reset bookings to before failed reschedule
      try {
        const defaultFilters = {
          page: 1,
          perPage: 25,
          order: 'id,desc',
        };
        const payload = await bookingService.getAllBookings(
          branch_id,
          defaultFilters
        );

        dispatch({
          type: BookingActionTypes.GET_ALL_BOOKINGS_SUCCESS,
          payload: payload,
        });
      } catch (error) {
        console.log('Get all bookings failed', error);
        dispatch({
          type: BookingActionTypes.GET_ALL_BOOKINGS_FAILURE,
          payload: error,
        });
      }
    }
  };
}

/**
 * Place a booking on hold
 *
 * @param {number} branch_id
 * @param {number} booking_id
 *
 * @returns {Promise<void>}
 */
export function holdBooking(branch_id: number, booking_id: number) {
  console.log(
    `Dispatch holdBooking action for branch id ${branch_id} and booking id ${booking_id}`
  );

  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      const { data } = await bookingService.holdBookingById(
        branch_id,
        booking_id
      );

      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
        payload: data,
      });
    } catch (error) {
      dispatch({
        type: BookingActionTypes.UPDATE_BOOKING_FAILURE,
        payload: error,
      });
    }
  };
}

/**
 * Handle insert and updates of async booking events
 * via pushers channels
 *
 * @param {number} branchId
 * @param {number} bookingId
 * @param {'create' | 'update' | 'delete'} action
 *
 * @returns {Promise<void>}
 */
export function handleAsyncBookingEvent(
  branchId: number,
  bookingId: number,
  action: 'create' | 'update' | 'delete'
) {
  return async (dispatch) => {
    dispatch({ type: BookingActionTypes.BOOKING_REQUEST });
    try {
      console.log(bookingId);
      let booking: Booking;

      switch (action) {
        case 'create':
          booking = await bookingService.getBookingById(branchId, bookingId);

          dispatch({
            type: BookingActionTypes.CREATE_BOOKING_SUCCESS,
            payload: booking,
          });
          break;
        case 'update':
          booking = await bookingService.getBookingById(branchId, bookingId);

          dispatch({
            type: BookingActionTypes.UPDATE_BOOKING_SUCCESS,
            payload: booking,
          });
          break;
        case 'delete':
          dispatch({
            type: BookingActionTypes.DELETE_BOOKING_SUCCESS,
            payload: bookingId,
          });
          break;
        default:
          return;
      }
    } catch (error) {
      console.log('async booking update failure');
      // dispatch({
      //   type: BookingActionTypes.BOO,
      //   payload: error,
      // });
    }
  };
}
